/**
 * Contrato de MUTABILIDADE dos campos da etiqueta — épico #323 §2.1, fase F1
 * (issue #324).
 *
 * ## Por que este módulo existe separado de `labelFieldContract.ts`
 *
 * Ele não importa **nada** — nem `zod`. É condição para que a suíte de
 * Firestore Rules (`firebase/test/rules`) possa derivar a matriz de casos
 * (campo × status × papel) do mesmo contrato que o servidor e os clientes
 * leem. Aquele pacote é um npm **isolado do workspace** (a CI roda `npm ci`
 * só dentro dele), então `zod` não resolve lá — um import de zod aqui
 * transformaria o teste derivado em cópia manual, e cópia manual de contrato
 * é exatamente o que esta fase existe para eliminar.
 *
 * O par `{ schema, mutability }` que o épico descreve continua existindo, em
 * `labelFieldContract.ts`, que junta o zod a este mapa.
 *
 * ## O modelo em uma frase
 *
 * `quantity` é o fato original (imutável após a impressão); `baixas[]` é o
 * ledger **append-only** dos fatos posteriores; `UNDO` é um fato compensatório;
 * todo o resto (`consumedQuantity`, `discardedQuantity`, `expiredQuantity`,
 * `remainingQuantity`, `overrunQuantity`, `status`) é **projeção do servidor**.
 */

import { LabelStatus, TenantRole } from './enums';

/**
 * Teto de entradas no ledger de um doc, estornos inclusos (~120 bytes por
 * entrada → ~60 KB no pior caso). As rules validam `size() <= LABEL_BAIXAS_MAX`.
 *
 * O teto de tamanho não é o primeiro problema que aparece: antes dele vêm
 * contenção no doc e o custo de recomputar o ledger efetivo a cada write. Por
 * isso o trigger emite `ledger_pressure` bem antes (300 movimentos ou >50
 * writes/dia) — é esse sinal, não este número, que dispara a conversa sobre
 * migrar `baixas` para subcoleção.
 */
export const LABEL_BAIXAS_MAX = 500;

/** Os 9 campos sob contrato. A lista É a fonte — implementação parcial é bug. */
export type LabelContractField =
  | 'quantity'
  | 'consumedQuantity'
  | 'discardedQuantity'
  | 'expiredQuantity'
  | 'remainingQuantity'
  | 'overrunQuantity'
  | 'baixas'
  | 'labelSchemaVersion'
  | 'movementReceivedAtMillis';

/** Desfechos: o que efetivamente consome saldo da etiqueta. */
export type LabelOutcomeKind = 'CONSUMED' | 'DISCARDED' | 'EXPIRED';
export const ALL_LABEL_OUTCOME_KINDS: readonly LabelOutcomeKind[] = [
  'CONSUMED',
  'DISCARDED',
  'EXPIRED',
];

/** Tipos de entrada do ledger: os três desfechos mais o estorno. */
export type LabelBaixaKind = LabelOutcomeKind | 'UNDO';
/** Tupla (não `readonly []`) porque `z.enum` exige pelo menos um elemento. */
export const ALL_LABEL_BAIXA_KINDS = [
  'CONSUMED',
  'DISCARDED',
  'EXPIRED',
  'UNDO',
] as const satisfies readonly LabelBaixaKind[];

/**
 * Versão do schema do doc da etiqueta. `1` = modelo antigo (só `status` e
 * `quantity`); `2` = ledger parcial. Nunca rebaixa.
 */
export const LABEL_SCHEMA_VERSION_V2 = 2;

/**
 * Status em que a etiqueta está fechada (vigente == 0). Inclui `MIXED`.
 *
 * O array é a fonte e o `Set` deriva dele — não o contrário. `JSON.stringify`
 * de um `Set` devolve `{}`, então o snapshot de contrato (#106 §9) precisa de
 * uma lista de verdade; derivar com spread daria ordem de inserção, e ordem
 * instável faria o gate `--check` falhar sem ninguém ter mudado nada.
 */
export const ALL_LABEL_CLOSED_STATUSES = [
  LabelStatus.CONSUMED,
  LabelStatus.DISCARDED,
  LabelStatus.EXPIRED,
  LabelStatus.MIXED,
] as const;

export const LABEL_CLOSED_STATUSES: ReadonlySet<string> = new Set<string>(
  ALL_LABEL_CLOSED_STATUSES,
);

/**
 * Ciclo de vida da etiqueta: para cada status, os status que podem vir DEPOIS.
 *
 * O ciclo é **monotônico** — a etiqueta avança e não volta — com exatamente
 * duas reversões, cada uma existindo porque um fato do mundo real a exige:
 *
 * - **`terminal → PRINTED`** é o Desfazer baixa (#230). Quem baixou errado
 *   precisa corrigir sem reimprimir: o QR e o PIN já estão colados na
 *   embalagem. `EXPIRED → PRINTED` tem gate extra — só com `expiresAtMillis`
 *   ainda no futuro, senão o cron re-expiraria em ≤30 min e devolveria
 *   alimento fora do prazo ao controle de validade.
 * - **`DELETED → PRINTED|CONSUMED|DISCARDED`** é o Recuperar / Recuperar e dar
 *   baixa (#148/#151). O soft-delete existe justamente porque a etiqueta
 *   física pode estar no estoque; sem a volta, o doc preservado não serviria
 *   para nada.
 *
 * O que a tabela BLOQUEIA, e que hoje o coringa `isManager` deixava passar:
 *
 * - **nada volta a `DRAFT`**. Rascunho é o estado de quem ainda não imprimiu;
 *   depois do papel na embalagem não há como desimprimir. Reusar clona um doc
 *   NOVO (`reusedFromLabelId`), não rebobina este.
 * - **rascunho não fecha nem se apaga por soft-delete**: `DRAFT` só vira
 *   `PRINTED`. Não há etiqueta física para consumir, e apagar rascunho é
 *   delete de verdade.
 * - **histórico não vira `DELETED`**. A tabela de ações da spec já dizia
 *   "`CONSUMED`/`DISCARDED`/`EXPIRED`: NINGUÉM apaga" — agora as rules dizem
 *   também.
 * - **`DELETED` não vai direto para `EXPIRED`/`MIXED`**: o que estava oculto
 *   volta pela recuperação, não por vencimento.
 *
 * Troca ENTRE terminais (`CONSUMED → MIXED`, `MIXED → EXPIRED`) é permitida
 * porque acontece de verdade: um movimento offline que chega depois do
 * fechamento, ou um estorno numa etiqueta vencida, muda a composição dos
 * desfechos. Mas ela só é alcançável pelos ramos do LEDGER — o coringa, que
 * mexe só em `status`, não pode transformar uma vencida em consumida à mão e
 * apagar uma perda.
 */
export const LABEL_STATUS_TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  [LabelStatus.DRAFT]: [LabelStatus.DRAFT, LabelStatus.PRINTED],
  [LabelStatus.PRINTED]: [
    LabelStatus.PRINTED,
    LabelStatus.CONSUMED,
    LabelStatus.DISCARDED,
    LabelStatus.EXPIRED,
    LabelStatus.MIXED,
    LabelStatus.DELETED,
  ],
  [LabelStatus.CONSUMED]: [
    LabelStatus.CONSUMED,
    LabelStatus.PRINTED,
    LabelStatus.DISCARDED,
    LabelStatus.EXPIRED,
    LabelStatus.MIXED,
  ],
  [LabelStatus.DISCARDED]: [
    LabelStatus.DISCARDED,
    LabelStatus.PRINTED,
    LabelStatus.CONSUMED,
    LabelStatus.EXPIRED,
    LabelStatus.MIXED,
  ],
  [LabelStatus.EXPIRED]: [
    LabelStatus.EXPIRED,
    LabelStatus.PRINTED,
    LabelStatus.CONSUMED,
    LabelStatus.DISCARDED,
    LabelStatus.MIXED,
  ],
  [LabelStatus.MIXED]: [
    LabelStatus.MIXED,
    LabelStatus.PRINTED,
    LabelStatus.CONSUMED,
    LabelStatus.DISCARDED,
    LabelStatus.EXPIRED,
  ],
  [LabelStatus.DELETED]: [
    LabelStatus.DELETED,
    LabelStatus.PRINTED,
    LabelStatus.CONSUMED,
    LabelStatus.DISCARDED,
  ],
};

/** `true` quando o ciclo de vida admite `before → after`. */
export function canTransitionStatus(before: string, after: string): boolean {
  return (LABEL_STATUS_TRANSITIONS[before] ?? []).includes(after);
}

/**
 * Os status que uma mudança **só de `status`** (o coringa `isManager`: nem
 * ledger, nem acumuladores) pode alcançar.
 *
 * Mais estreita que a tabela geral de propósito. Sair de um terminal para
 * OUTRO terminal muda a composição dos desfechos, e composição é coisa do
 * ledger: fazê-lo à mão transformaria uma vencida em consumida e apagaria uma
 * perda do relatório, sem nenhum fato que a justificasse. Por aqui, de uma
 * fechada só se volta para `PRINTED` — que é o Desfazer, e tem gate próprio.
 *
 * `MIXED` não volta nem para `PRINTED` (#795, 1º nível item 5, 2026-10-07
 * 13:20 +0200): não existe desfazer a etiqueta mista, e o trigger traduzia o
 * status solto num estorno do ÚLTIMO desfecho — numa mista com vencimento, o
 * do cron, e o saldo vencido voltava válido. Estorno de baixa da mista é pelo
 * ledger. Espelhos: `firebase/firestore.rules` (coringa `isManager`) e
 * `LabelRules.canTransitionStatusByFieldOnly` no iOS (conferido contra
 * `contract/decisions.json`).
 */
export function canTransitionStatusByFieldOnly(before: string, after: string): boolean {
  if (!canTransitionStatus(before, after)) return false;
  if (before === LabelStatus.MIXED) return after === before;
  if (LABEL_CLOSED_STATUSES.has(before)) {
    return after === before || after === LabelStatus.PRINTED;
  }
  return true;
}

export type LabelFieldMutability = {
  /** Status em que o campo aceita valor livre do cliente. Vazio = nunca. */
  readonly editableWhile: readonly string[];
  /** A partir deste status o campo é congelado (documental; o gate é `editableWhile`). */
  readonly immutableAfter?: string;
  /** Campo é projeção do ledger — o trigger reconcilia e é a autoridade final. */
  readonly derivedFrom?: 'baixas';
  /** Nenhum cliente escreve, em ramo nenhum: as rules barram. Só o Admin SDK. */
  readonly serverOnly?: boolean;
  /** Valor carimbado no create (pelo cliente v2 ou pelo trigger normalizador). */
  readonly setOnCreate?: number;
  /** Entrada nunca é editada nem removida; só acrescentada. */
  readonly appendOnly?: boolean;
  /** Permissão NORMAL de append por `kind` — é o que a UI oferece. */
  readonly appendWhile?: Readonly<Partial<Record<LabelBaixaKind, readonly string[]>>>;
  /**
   * Exceção TÉCNICA de reconciliação (épico §5.1): write enfileirado offline
   * que chega **depois** de a etiqueta fechar. Não é permissão de produto — a
   * UI nunca oferece a ação; existe porque rejeitar um write da fila é perda
   * SILENCIOSA de um fato (o SDK só entrega a rejeição a um listener que ainda
   * esteja vivo). As rules aceitam; o trigger reconcilia e sinaliza.
   */
  readonly acceptLateOfflineMovement?: Readonly<Partial<Record<LabelBaixaKind, boolean>>>;
  /** Status em que uma entrada pode ser removida. Sempre vazio: o ledger é imutável. */
  readonly removeWhile?: readonly string[];
};

/**
 * O contrato. Consumidores: (a) a matriz derivada em
 * `firebase/test/rules/labels.test.ts`, (b) os guards de cliente (web e o
 * espelho Kotlin `LabelFieldContract.kt`), (c) o trigger, que só reconcilia
 * campo marcado `derivedFrom`.
 *
 * Sobre `serverOnly` em `expiredQuantity` e `overrunQuantity`: o épico os
 * declara `derivedFrom: 'baixas'` com `editableWhile: []`, e diz em §4.2 que o
 * cliente **nunca** os escreve. Isso os separa de `consumedQuantity`,
 * `discardedQuantity` e `remainingQuantity`, que o cliente **grava
 * otimisticamente** dentro dos ramos de movimento/estorno para a UI offline
 * refletir na hora. `serverOnly` é o nome dessa diferença — sem ele o teste
 * derivado não conseguiria distinguir os dois grupos.
 */
export const LABEL_FIELD_MUTABILITY: Readonly<Record<LabelContractField, LabelFieldMutability>> = {
  /** ORIGINAL — o que foi impresso no papel. Nunca muda depois disso. */
  quantity: {
    editableWhile: [LabelStatus.DRAFT],
    immutableAfter: LabelStatus.PRINTED,
  },
  /** Σ dos `CONSUMED` efetivos. Cache do ledger; o cliente grava o otimista. */
  consumedQuantity: {
    editableWhile: [],
    derivedFrom: 'baixas',
  },
  /** Σ dos `DISCARDED` efetivos. Cache do ledger; o cliente grava o otimista. */
  discardedQuantity: {
    editableWhile: [],
    derivedFrom: 'baixas',
  },
  /** Σ dos `EXPIRED` efetivos. Só o servidor gera a entrada e o cache. */
  expiredQuantity: {
    editableWhile: [],
    derivedFrom: 'baixas',
    serverOnly: true,
  },
  /**
   * SALDO VIGENTE materializado — o campo mais lido do doc (estoque, listas,
   * agrupamento, scanner, detalhe, alertas, Monitor). Calculado **direto do
   * ledger**, nunca a partir dos outros caches.
   */
  remainingQuantity: {
    editableWhile: [],
    derivedFrom: 'baixas',
  },
  /** `max(0, Σ efetivo − quantity)` — anomalia auditável do race offline (§5.1). */
  overrunQuantity: {
    editableWhile: [],
    derivedFrom: 'baixas',
    serverOnly: true,
  },
  /** O ledger. Fonte da verdade; imutável; só cresce. */
  baixas: {
    appendOnly: true,
    appendWhile: {
      CONSUMED: [LabelStatus.PRINTED],
      DISCARDED: [LabelStatus.PRINTED],
      // Só o servidor (cron de vencimento / trigger) — nunca um cliente.
      EXPIRED: [LabelStatus.PRINTED],
      UNDO: [
        LabelStatus.PRINTED,
        LabelStatus.CONSUMED,
        LabelStatus.DISCARDED,
        LabelStatus.EXPIRED,
        LabelStatus.MIXED,
      ],
    },
    acceptLateOfflineMovement: {
      CONSUMED: true,
      DISCARDED: true,
    },
    removeWhile: [],
    editableWhile: [],
  },
  /** 1 = modelo antigo · 2 = ledger parcial. Carimbado no create; nunca rebaixa. */
  labelSchemaVersion: {
    editableWhile: [],
    setOnCreate: LABEL_SCHEMA_VERSION_V2,
  },
  /**
   * `baixaId → horário de INGESTÃO no servidor`. Event time (`atMillis`) ×
   * ingestion time: as decisões são tomadas pelo declarado e **sinalizadas**
   * pelo recebido. É só observabilidade — nenhuma decisão de vigente, status ou
   * excesso lê este mapa, e chave ausente é tolerada e autorreparável.
   */
  movementReceivedAtMillis: {
    editableWhile: [],
    serverOnly: true,
    derivedFrom: 'baixas',
  },
};

/** `true` quando nenhum cliente pode escrever o campo, em ramo nenhum. */
export function isServerOnlyLabelField(field: LabelContractField): boolean {
  return LABEL_FIELD_MUTABILITY[field].serverOnly === true;
}

/**
 * `true` quando o campo aceita valor **livre** do cliente naquele status.
 *
 * Cuidado: os caches otimistas (`consumedQuantity`, `discardedQuantity`,
 * `remainingQuantity`) respondem `false` aqui e ainda assim são escritos pelo
 * cliente — mas só **dentro** dos ramos de movimento/estorno, amarrados à
 * entrada nova do ledger. "Editar o campo" e "mover o cache junto com um fato"
 * são coisas diferentes.
 */
export function canEditField(field: LabelContractField, status: string): boolean {
  const mutability = LABEL_FIELD_MUTABILITY[field];
  if (mutability.serverOnly === true) return false;
  return mutability.editableWhile.includes(status);
}

/** Atalho de guard de cliente: o campo Quantidade só é editável no rascunho. */
export function canEditQuantity(status: string): boolean {
  return canEditField('quantity', status);
}

/**
 * `true` quando um cliente com aquele papel pode acrescentar uma entrada do
 * `kind` numa etiqueta naquele status.
 *
 * - `EXPIRED` é sempre `false`: vencer é desfecho que só o servidor registra.
 * - `UNDO` é de OWNER/MANAGER, em qualquer status exceto `DRAFT`/`DELETED`.
 * - `CONSUMED`/`DISCARDED` são de qualquer member em `PRINTED` (permissão
 *   normal) **e** em etiqueta já fechada (exceção `acceptLateOfflineMovement`
 *   — o write que estava na fila offline). As rules ainda exigem, na fechada
 *   por vencimento, que `atMillis` seja anterior à validade.
 */
export function canAppendKind(kind: LabelBaixaKind, status: string, role: string): boolean {
  const { appendWhile, acceptLateOfflineMovement } = LABEL_FIELD_MUTABILITY.baixas;
  if (kind === 'EXPIRED') return false;
  const allowedStatuses = appendWhile?.[kind] ?? [];
  if (kind === 'UNDO') {
    const isManager = role === TenantRole.OWNER || role === TenantRole.MANAGER;
    return isManager && allowedStatuses.includes(status);
  }
  const isMember =
    role === TenantRole.OWNER || role === TenantRole.MANAGER || role === TenantRole.STAFF;
  if (!isMember) return false;
  if (allowedStatuses.includes(status)) return true;
  return acceptLateOfflineMovement?.[kind] === true && LABEL_CLOSED_STATUSES.has(status);
}
